/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V54 - LINTING & NAVIGATION FIX) ---
 * - MISSION: To fix the cross-page navigation bug while also satisfying the
 * strict `react-hooks/exhaustive-deps` linting rule.
 *
 * - THE BUG: The previous version fixed the navigation logic but introduced a
 * linting warning by not including `location.pathname` in a `useEffect`
 * dependency array, which would fail the build due to the `--max-warnings 0` flag.
 *
 * - THE FIX (Unified Navigation & Linting Compliance):
 * 1.  NEW `useEffect` HOOK: A new effect has been added that specifically
 * watches for `location.state.pendingNav`. When this component mounts (e.g.,
 * after navigating from `/login` to `/`), this effect checks if a pending
 * navigation object exists in the location state.
 * 2.  STATE TRANSFER: If it finds one, it immediately transfers it to the central
 * `pendingNavigation` state using `setPendingNavigation`.
 * 3.  STATE CLEARING: It then uses `Maps(location.pathname, { replace: true, state: {} })`
 * to clear the state from the URL's history, preventing the scroll from
 * re-triggering on a page refresh.
 * 4.  LINTING FIX: The dependency array for this new effect has been updated
 * to `[location.state, location.pathname, setPendingNavigation, navigate]`,
 * which now correctly includes all dependencies and resolves the linting error.
 *
 * - RESULT: The implementation is now complete and production-ready. The
 * homepage reliably handles scroll requests from any page, the lazy-loading
 * logic is respected, and the code passes all linting rules.
 */
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCarousel } from '@/context/CarouselContext';
import { useNavContext } from '@/context/NavigationProvider';
import { useModal } from '@/context/ModalContext';
import { useGuestUser } from '@/hooks/useGuestUser';
import { redeemResumeToken } from '@/services/api';

// --- Static Import (Above the Fold) ---
import Hero from '../components/home/Hero';
// import AnnouncementBanner from '../components/home/AnnouncementBanner';
import LazyLoad from '../components/shared/LazyLoad';

// --- Dynamically Imported Section Components ---
const IndikinPass = lazy(() => import('../components/home/IndikinPass'));
const EcosystemComponents = lazy(() => import('../components/home/EcosystemComponents'));
const FoundingFilmmakers = lazy(() => import('../components/home/FoundingFilmmakers'));
const Ecosystem = lazy(() => import('../components/home/Ecosystem'));
const FeaturedFilms = lazy(() => import('../components/home/FeaturedFilms'));
const RevenueSharing = lazy(() => import('../components/home/RevenueSharing'));
const FilmmakerCTA = lazy(() => import('../components/home/FilmmakerCTA'));
const PaymentOptions = lazy(() => import('../components/home/PaymentOptions'));
const NewsletterSignup = lazy(() => import('../components/home/NewsletterSignup'));

const SectionLoader = () => (
  <div style={{ minHeight: '60vh' }} className="w-full flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const sectionOrder = [
  { id: 'featured-films', carouselId: 'featured-films-carousel' },
  { id: 'founding-filmmakers', carouselId: 'filmmakers-carousel' },
  { id: 'indikin-pass', carouselId: 'indikin-pass-carousel' },
  { id: 'ecosystem-components' },
  { id: 'ecosystem', domId: 'indikin-ecosystem-section' },
  { id: 'revenue-sharing' },
  { id: 'filmmaker-cta' },
  { id: 'payment-options' },
  { id: 'newsletter-signup' },
];

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setTargetSlide, carouselReadyState } = useCarousel();
  const { t } = useTranslation();
  const { pendingNavigation, setPendingNavigation, clearPendingNavigation, setTargetToken } = useNavContext();
  const { openModal } = useModal();
  const { setGuestEmail } = useGuestUser();
  const resumeRef = useRef(false);

  const [forceVisibleSections, setForceVisibleSections] = useState({});

  useEffect(() => {
    document.title = t('legal.homeTitle');
  }, [t]);

  // --- REVISED: Effect to handle SECURE (simple token) payment resumption ---
  useEffect(() => {
    const resumePayment = async () => {
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('resume_token');

      if (token && !resumeRef.current) {
        resumeRef.current = true;
        try {
          const details = await redeemResumeToken(token);
          if (details && details.tier) {
            // Set guest email BEFORE opening the modal
            setGuestEmail(details.email);

            const tierForModal = {
              id: details.tier.id,
              title: details.tier.name,
              price: Number(details.resumptionData.totalAmount || 0).toFixed(2),
            };

            openModal('payment', {
              tier: tierForModal,
              initialTab: 'crypto',
              resumptionData: {
                ...details.resumptionData,
                orderId: details.orderId,
              },
            });
          }
        } catch (error) {
          console.error('Failed to resume payment:', error.message || 'An unknown error occurred.');
          // Optionally, you could open a modal here to show the error to the user.
        } finally {
          // Clean the token from the URL to prevent re-triggering
          urlParams.delete('resume_token');
          navigate({ search: urlParams.toString() }, { replace: true });
        }
      }
    };

    resumePayment();
  }, [location.search, navigate, openModal, setGuestEmail]);

  // --- FIX: Effect to handle incoming navigation state from other pages ---
  useEffect(() => {
    if (location.state?.pendingNav) {
      setPendingNavigation(location.state.pendingNav);
      // Clear the state from location history to prevent re-triggering on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, setPendingNavigation, navigate]);

  // --- NEW: Effect to handle Hash Navigation (Deep Linking) ---
  // This allows external links (and the AI) to navigate to specific sections via URL hashes.
  // Example: indikin.online/#indikin-pass-carousel
  // Example: indikin.online/?slide=Tom%20Gillespie#founding-filmmakers
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      // Check if the hash corresponds to a valid section or carousel
      const targetSection = sectionOrder.find(s => s.id === hashId || s.carouselId === hashId);

      if (targetSection) {
        // Use declared domId if available (for ID mismatches like ecosystem), otherwise hashId
        let targetId = targetSection.domId || hashId;

        // Parse Query Params for specific slide navigation
        const searchParams = new URLSearchParams(location.search);
        const slideName = searchParams.get('slide');
        let slide = undefined;

        if (slideName) {
          // Determine slide type based on section
          // Currently only Founding Filmmakers supports this deep link pattern via name
          if (hashId === 'founding-filmmakers' || hashId === 'filmmakers-carousel') {
            slide = { type: 'storymaker', name: slideName };
            // FORCE targetId to be the carousel ID for the ready check to work
            if (targetSection.carouselId) {
              targetId = targetSection.carouselId;
            }
          }
          else if (hashId === 'featured-films' || hashId === 'featured-films-carousel') {
            slide = { type: 'film', title: slideName };
            // FORCE targetId to be the carousel ID for the ready check to work
            if (targetSection.carouselId) {
              targetId = targetSection.carouselId;
            }
          }
        }


        // Check for 'token' param (for Ecosystem)
        const tokenSymbol = searchParams.get('token');
        let hasSubNav = false;
        if (tokenSymbol && hashId === 'ecosystem') {
          setTargetToken(tokenSymbol);
          hasSubNav = true;
        }

        setPendingNavigation({ targetId, slide, hasSubNav });
      }
    }
  }, [location.hash, location.search, setPendingNavigation, setTargetToken]);

  // Effect to process pending navigation requests (from context or location state)
  useEffect(() => {
    if (!pendingNavigation) return;

    // --- DEFINITIVE FIX: Unify lazy-load handling ---
    // Find the index of the target section, whether it's a plain section or a carousel.
    const targetIndex = sectionOrder.findIndex(section =>
      section.id === pendingNavigation.targetId ||
      section.carouselId === pendingNavigation.targetId ||
      section.domId === pendingNavigation.targetId
    );

    // If the target section exists in our defined order, force it and all preceding sections to render.
    if (targetIndex !== -1) {
      const sectionsToForce = {};
      for (let i = 0; i <= targetIndex; i++) {
        sectionsToForce[sectionOrder[i].id] = true;
      }
      setForceVisibleSections(sectionsToForce);
    }
    // --- END FIX ---

    let attempts = 0;
    const maxAttempts = 40; // 2 seconds timeout

    const pollForReady = setInterval(() => {
      const element = document.getElementById(pendingNavigation.targetId);
      attempts++;

      if (element) {
        // Logic for carousel navigation (from search results)
        if (pendingNavigation.slide !== undefined) {
          const isCarouselReady = carouselReadyState[pendingNavigation.targetId];
          if (isCarouselReady) {
            clearInterval(pollForReady);

            // Fix: True Vertical Centering logic
            const headerHeight = 85;
            const elementHeight = element.offsetHeight;
            const windowHeight = window.innerHeight;

            // Calculate space available below header
            const availableSpace = windowHeight - headerHeight;

            let y;
            if (elementHeight < availableSpace) {
              // If element fits, center it in the available space
              const topMargin = (availableSpace - elementHeight) / 2;
              y = element.getBoundingClientRect().top + window.scrollY - (headerHeight + topMargin);
            } else {
              // If element is taller than screen, align top with header bottom
              y = element.getBoundingClientRect().top + window.scrollY - headerHeight;
            }

            window.scrollTo({ top: y, behavior: 'smooth' });

            setTargetSlide(pendingNavigation.slide);
            clearPendingNavigation();
          }
        }
        // Logic for simple section scroll (from hero button)
        else {
          clearInterval(pollForReady);



          // --- ROBUST SCROLL: Progressive "Chase" Logic ---
          // Allows layout to shift (lazy loading) while keeping target in view.
          let chaseAttempts = 0;
          // FIX: If we have sub-navigation (like focusing a token), stop chasing sooner (1s)
          // so the child component (Ecosystem.jsx) can take over scrolling control.
          // Otherwise, stick to 5s to handle heavy lazy loading shifts.
          const maxChaseAttempts = pendingNavigation.hasSubNav ? 5 : 25;
          const chaseInterval = 200;
          let chaseTimeout = null;
          let userStopped = false;

          const stopChase = () => {
            userStopped = true;
            if (chaseTimeout) clearTimeout(chaseTimeout);
            window.removeEventListener('wheel', stopChase);
            window.removeEventListener('touchmove', stopChase);
            window.removeEventListener('keydown', stopChase);
          };

          // Listen for manual interaction (wheel, touch, keys) to interrupt the auto-scroll
          window.addEventListener('wheel', stopChase);
          window.addEventListener('touchmove', stopChase);
          window.addEventListener('keydown', stopChase);

          const doProgressiveScroll = () => {
            if (userStopped) return;

            // Execute scroll
            // Fix: Manual scroll with offset for sticky header
            const yOffset = -100;
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });

            chaseAttempts++;

            if (chaseAttempts < maxChaseAttempts && !userStopped) {
              chaseTimeout = setTimeout(doProgressiveScroll, chaseInterval);
            } else {
              // Cleanup listeners when finished or timed out
              window.removeEventListener('wheel', stopChase);
              window.removeEventListener('touchmove', stopChase);
              window.removeEventListener('keydown', stopChase);
            }
          };

          doProgressiveScroll();
          clearPendingNavigation();
        }
      }

      if (attempts > maxAttempts) {
        clearInterval(pollForReady);
        console.error(`Navigation timed out for target: "${pendingNavigation.targetId}"`);
        clearPendingNavigation();
      }
    }, 50);

    return () => clearInterval(pollForReady);

  }, [pendingNavigation, clearPendingNavigation, setTargetSlide, carouselReadyState, setForceVisibleSections]);


  return (
    <>
      <div className="flex flex-col">
        <div className="order-1">
          <Hero />
        </div>
        <div className="order-2 md:order-[-1]">
          {/* <AnnouncementBanner /> */}
        </div>
      </div>

      <LazyLoad minHeight="600px" forceVisible={forceVisibleSections['featured-films']}>
        <Suspense fallback={<SectionLoader />}>
          <FeaturedFilms forceInteractive={!!forceVisibleSections['featured-films']} />
        </Suspense>
      </LazyLoad>

      <LazyLoad minHeight="600px" forceVisible={forceVisibleSections['founding-filmmakers']}>
        <Suspense fallback={<SectionLoader />}>
          <FoundingFilmmakers forceInteractive={!!forceVisibleSections['founding-filmmakers']} />
        </Suspense>
      </LazyLoad>

      <LazyLoad minHeight="800px" forceVisible={forceVisibleSections['indikin-pass']}>
        <Suspense fallback={<SectionLoader />}>
          <IndikinPass forceInteractive={!!forceVisibleSections['indikin-pass']} />
        </Suspense>
      </LazyLoad>

      <LazyLoad minHeight="600px" forceVisible={forceVisibleSections['ecosystem-components']}>
        <Suspense fallback={<SectionLoader />}>
          <EcosystemComponents />
        </Suspense>
      </LazyLoad>

      <LazyLoad forceVisible={forceVisibleSections['ecosystem']}>
        <Suspense fallback={<SectionLoader />}>
          <Ecosystem />
        </Suspense>
      </LazyLoad>

      <LazyLoad forceVisible={forceVisibleSections['revenue-sharing']}>
        <Suspense fallback={<SectionLoader />}>
          <RevenueSharing />
        </Suspense>
      </LazyLoad>

      <LazyLoad forceVisible={forceVisibleSections['filmmaker-cta']}>
        <Suspense fallback={<SectionLoader />}>
          <FilmmakerCTA />
        </Suspense>
      </LazyLoad>

      <LazyLoad forceVisible={forceVisibleSections['payment-options']}>
        <Suspense fallback={<SectionLoader />}>
          <PaymentOptions />
        </Suspense>
      </LazyLoad>

      <LazyLoad forceVisible={forceVisibleSections['newsletter-signup']}>
        <Suspense fallback={<SectionLoader />}>
          <NewsletterSignup />
        </Suspense>
      </LazyLoad>
    </>
  );
};

export default Home;
